import { apiSlice } from "../../app/api/apiSlice";
import { urlConfig } from "../../config";

// Types
export interface AirtimePreviewRequest {
  product: "airtime";
  phone: string;
  service_id: string;
  amount: number;
}

export interface DataPreviewRequest {
  product: "data";
  phone: string;
  service_id: string;
  variation_id: string;
}

export interface ElectricityPreviewRequest {
  product: "electricity";
  customer_id: string;
  service_id: string;
  variation_id: string;
  amount: number;
}

export interface TVPreviewRequest {
  product: "tv";
  customer_id: string;
  service_id: string;
  variation_id: string;
  subscription_type?: "renew" | "change";
  tv_amount?: number;
}

export interface EPinsPreviewRequest {
  product: "epins";
  service_id: string;
  value: number;
  quantity: number;
}

export interface BettingPreviewRequest {
  product: "betting";
  customer_id: string;
  service_id: string;
  amount: number;
}

export interface PreviewResponse {
  product: string;
  serviceName: string;
  fee: number;
  base: number;
  total: number;
  premium: number;
  details: {
    phone?: string;
    customer_id?: string;
    service_id: string;
    variation_id?: string;
    subscription_type?: string;
    tv_amount?: number;
    value?: number;
    quantity?: number;
    amount?: number;
  };
  sufficient: boolean;
}

export interface DataVariation {
  variation_id: number;
  service_name: string;
  service_id: string;
  data_plan: string;
  price: string;
  reseller_price: string;
  availability: string;
}

export interface TVVariation {
  variation_id: number;
  service_name: string;
  service_id: string;
  package_bouquet: string;
  price: string;
  availability: string;
}

export interface DataVariationsResponse {
  code: string;
  message: string;
  product: string;
  data: DataVariation[];
}

export interface TVVariationsResponse {
  code: string;
  message: string;
  product: string;
  data: TVVariation[];
}

export interface EbillsTransaction {
  request_id: string;
  product: string;
  status: string;
  amount: number;
  fee: number;
  total: number;
  service_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface EbillsTransactionsResponse {
  transactions: EbillsTransaction[];
  total: number;
  page: number;
  limit: number;
}

export interface EbillPin {
  amount: string;
  pin: string;
  instruction: string;
  serial: string;
}

export interface EbillsTransactionDetails {
  request_id: string;
  order_id: string;
  product: string;
  status: string;
  base_amount: number;
  fee: number;
  total: number;
  created_at: string;
  updated_at: string;
  service_id: string | null;
  phone: string | null;
  customer_id: string | null;
  variation_id: string | null;
  subscription_type: string | null;
  value: number | null;
  quantity: number | null;
  epins?: EbillPin[];
  api_data?: {
    order_id: number;
    status: string;
    product_name: string;
    service_name: string;
  };
}

export const ebillsSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // Get data variations
    getDataVariations: build.query<DataVariationsResponse, string>({
      query: (service_id) => ({
        url: `${urlConfig.ebillsUrl}/ebills/variations/data?service_id=${service_id}`,
        method: "GET",
      }),
    }),

    // Get TV variations
    getTVVariations: build.query<TVVariationsResponse, string>({
      query: (service_id) => ({
        url: `${urlConfig.ebillsUrl}/ebills/variations/tv?service_id=${service_id}`,
        method: "GET",
      }),
    }),

    // Preview airtime
    previewAirtime: build.mutation<PreviewResponse, AirtimePreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Preview data
    previewData: build.mutation<PreviewResponse, DataPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Preview electricity
    previewElectricity: build.mutation<
      PreviewResponse,
      ElectricityPreviewRequest
    >({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Preview TV
    previewTV: build.mutation<PreviewResponse, TVPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Preview ePins
    previewEPins: build.mutation<PreviewResponse, EPinsPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Preview betting
    previewBetting: build.mutation<PreviewResponse, BettingPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/preview`,
        method: "POST",
        body,
      }),
    }),

    // Confirm airtime
    confirmAirtime: build.mutation<any, AirtimePreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Confirm data
    confirmData: build.mutation<any, DataPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Confirm electricity
    confirmElectricity: build.mutation<any, ElectricityPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Confirm TV
    confirmTV: build.mutation<any, TVPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Confirm ePins
    confirmEPins: build.mutation<any, EPinsPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Confirm betting
    confirmBetting: build.mutation<any, BettingPreviewRequest>({
      query: (body) => ({
        url: `${urlConfig.ebillsUrl}/ebills/confirm`,
        method: "POST",
        body,
      }),
    }),

    // Get transactions
    getTransactions: build.query<
      EbillsTransactionsResponse,
      { page?: number; limit?: number }
    >({
      query: ({ page = 1, limit = 20 }) => ({
        url: `${urlConfig.ebillsUrl}/ebills/transactions?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: ["Ebills"],
    }),

    // Get transaction details
    getTransactionDetails: build.query<EbillsTransactionDetails, string>({
      query: (requestId) => ({
        url: `${urlConfig.ebillsUrl}/ebills/transactions/${requestId}`,
        method: "GET",
      }),
      providesTags: (result, error, requestId) => [
        { type: "Ebills", id: requestId },
      ],
    }),
  }),
});

export const {
  useGetDataVariationsQuery,
  useGetTVVariationsQuery,
  usePreviewAirtimeMutation,
  usePreviewDataMutation,
  usePreviewElectricityMutation,
  usePreviewTVMutation,
  usePreviewEPinsMutation,
  usePreviewBettingMutation,
  useConfirmAirtimeMutation,
  useConfirmDataMutation,
  useConfirmElectricityMutation,
  useConfirmTVMutation,
  useConfirmEPinsMutation,
  useConfirmBettingMutation,
  useGetTransactionsQuery,
  useGetTransactionDetailsQuery,
} = ebillsSlice;
